/**
 * WHICH LINKS IN A MESSAGE ARE LISTINGS OF OURS.
 *
 * A MIRROR of api/src/lib/messages-listing-link-rules.ts, which is where the rule is
 * tested and where it is decided which cards a message shows: the server reads
 * every message's links when the conversation is read and sends the ids. This
 * copy answers only the two questions the server's answer cannot — where in the
 * text a link sits, so it can be shortened in place, and what the composer is
 * about to send, so it can be previewed before it goes. Keep the two in step.
 *
 * Our four hosts, and the one the app is being served from (so dev and the
 * test stack work); the listing's own page only, with any query string or
 * fragment; a scheme required; the first three distinct listings.
 */

export const MAX_LISTING_CARDS = 3;

const LISTING_HOSTS = [
  'cardboardmarket.xyz',
  'app.cardboardmarket.xyz',
  'pre.cardboardmarket.xyz',
  'pre-app.cardboardmarket.xyz',
];

const URL_LIKE = /https?:\/\/[^\s<>"'`]+/gi;
const TRAILING = /[.,;:!?¡¿)\]}»]+$/;
const LISTING_PATH = /^\/listing\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

function hosts() {
  const own = typeof window !== 'undefined' && window.location ? window.location.host.toLowerCase() : '';
  return new Set(own ? [...LISTING_HOSTS, own] : LISTING_HOSTS);
}

/** Every listing link in `text`, in order: `{ id, url, start, end }`. */
export function listingLinksIn(text) {
  const ours = hosts();
  const out = [];
  for (const match of String(text || '').matchAll(URL_LIKE)) {
    const url = match[0].replace(TRAILING, '');
    let parsed;
    try { parsed = new URL(url); } catch { continue; }
    if (!ours.has(parsed.host.toLowerCase())) continue;
    const path = LISTING_PATH.exec(parsed.pathname);
    if (!path) continue;
    out.push({ id: path[1].toLowerCase(), url, start: match.index, end: match.index + url.length });
  }
  return out;
}

/** The first listing a draft names, or null: what the composer previews. */
export function firstListingIn(text) {
  const [first] = listingLinksIn(text);
  return first ? first.id : null;
}

/**
 * The link as a person reads it in a bubble: `cardboardmarket.xyz/listing/8f2c…`.
 * The host without its `app.`, and enough of the id to tell two apart.
 */
export function shortListingLink(link) {
  let host = '';
  try { host = new URL(link.url).host.replace(/^app\./, ''); } catch { host = ''; }
  return `${host}/listing/${link.id.slice(0, 4)}…`;
}
